<template>
  <k-panel
    class="learner-rag"
    title="RAG Status"
    :show-content="showContent"
    @toggle="showContent = $event"
  >
    <template #body>
      <div v-if="!ready" class="content-spinner" role="status" aria-label="Loading">
        <i class="fa-duotone fa-spinner fa-spin" aria-hidden="true"></i>
      </div>
      <div v-else-if="ragData" class="rag-content">
        <div class="overall-rag">
          <span class="overall-rag-label">Overall RAG</span>
          <k-status-pill
            class="overall-rag-pill"
            :type="overallRagPillType"
            :status-value="overallRagPillUsesCustomLabel ? '' : overallRagLabel"
          >
            <template v-if="overallRagPillUsesCustomLabel" #label>
              {{ overallRagLabel }}
            </template>
          </k-status-pill>
        </div>
        <ul class="rag-metrics">
          <li v-for="metric in ragMetrics" :key="metric.label">
            <span class="rag-metric-label">{{ metric.label }}</span>
            <div class="rag-metric-value">
              <div v-if="metric.type === 'progress'" class="progress-metric-header">
                <k-progress
                  class="progress-metric-circle"
                  shape="circle"
                  size="xsmall"
                  :percentage="metric.percentage"
                  :precision="0"
                  :aria-label="metric.ariaLabel"
                />
                <span class="progress-metric-percentage">{{ metric.percentageLabel }}</span>
              </div>
              <k-rag-dot
                v-else
                :rag-status="metric.ragStatus"
                :metric-value="metric.metricValue"
                :metric-type="metric.metricType"
              />
              <span v-if="metric.detail" class="rag-metric-detail">{{ metric.detail }}</span>
            </div>
          </li>
        </ul>
      </div>
      <empty-placeholder
        v-else
        title="No RAG data found"
        info="RAG status is not available for this learner on the selected programme"
      />
    </template>
  </k-panel>
</template>

<style scoped>
.learner-rag {
  height: 100%;
  margin: 0;
  box-sizing: border-box;
}

.learner-rag :deep(.panel-body) {
  padding: 15px;
}

.content-spinner {
  text-align: center;
  font-size: 1.5em;
  width: 100%;
}

.rag-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.overall-rag {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 0;
  border-bottom: 1px solid var(--kate-mono-2);
}

.overall-rag-label {
  font-size: 1.2rem;
  font-weight: bold;
}

.overall-rag-pill :deep(.status-pill) {
  font-size: 1rem;
  font-weight: bold;
  padding: 6px 14px;
}

.rag-metrics {
  list-style: none;
  padding: 10px 0 10px 20px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rag-metrics li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.rag-metric-label {
  font-weight: bold;
}

.rag-metric-value {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.rag-metric-detail {
  font-size: 0.85rem;
  color: var(--kate-type-light);
}

.progress-metric-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-metric-circle :deep(.progress-circle-number) {
  display: none;
}

.progress-metric-circle :deep(.progress-circle-x-small) {
  width: 32px;
}

.progress-metric-circle :deep(.progress-circle-fill svg) {
  width: 32px;
  height: 32px;
}

.progress-metric-circle :deep(.progress-circle-x-small .progress-circle-fill circle) {
  stroke-width: 4px;
}

.progress-metric-percentage {
  font-weight: bold;
}
</style>

<script>
import KPanel from '@base-components/k-panel.vue';
import KRagDot from '@base-components/k-rag-dot.vue';
import KStatusPill from '@base-components/k-status-pill.vue';
import KProgress from '@base-components/k-progress.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import TimeMixin from '@mixins/time-mixins';
import {
  getAssignmentCompletionPercentage,
  getAssignmentsSummary,
  getBilMetricDetail,
  getFundingMetricDetail,
  getOverallRagLabel,
  getOverallRagPillType,
  getOofMetricValue,
  getOtjHoursSummary,
  getRagStatus,
  getWorkshopsSummary,
  isFirst42DayExclusion,
  isOnABreak,
  overallRagPillUsesCustomLabel,
} from '@utils/rag-metrics';

export default {
  components: {
    KPanel,
    KRagDot,
    KStatusPill,
    KProgress,
    EmptyPlaceholder,
  },

  mixins: [TimeMixin],

  props: {
    studentId: {
      type: Number,
    },
    programmeId: {
      type: Number,
    },
  },

  data() {
    return {
      showContent: false,
      ragData: null,
      ready: false,
    };
  },

  computed: {
    isFirst42() {
      return isFirst42DayExclusion(this.ragData);
    },

    overallRagLabel() {
      return getOverallRagLabel(this.ragData);
    },

    overallRagPillType() {
      return getOverallRagPillType(this.overallRagLabel);
    },

    overallRagPillUsesCustomLabel() {
      return overallRagPillUsesCustomLabel(this.overallRagLabel);
    },

    ragMetrics() {
      if (!this.ragData) {
        return [];
      }

      return [
        {
          label: 'OTJ On-Track',
          ragStatus: getRagStatus(this.ragData, 'otj_rag'),
          metricValue: this.ragData.perc_otj_hours,
          metricType: 'percentage',
          detail: this.otjHoursSummary,
        },
        {
          label: 'Workshop Attendance',
          ragStatus: getRagStatus(this.ragData, 'workshop_rag'),
          metricValue: this.ragData.workshops_completed_percentage,
          metricType: 'percentage',
          detail: this.workshopsSummary,
        },
        {
          label: 'On a Break',
          ragStatus: getRagStatus(this.ragData, 'bil_rag'),
          metricValue: this.isOnABreak ? 'Yes' : 'No',
          metricType: 'string',
          detail: this.bilMetricDetail,
        },
        {
          label: 'Funding Status',
          ragStatus: getRagStatus(this.ragData, 'oof_rag'),
          metricValue: this.oofMetricValue,
          metricType: 'string',
          detail: this.fundingMetricDetail,
        },
        {
          label: 'Assignment Completion',
          type: 'progress',
          percentage: this.assignmentCompletionPercentage,
          percentageLabel: this.assignmentCompletionPercentageLabel,
          ariaLabel: 'Assignment completion percentage',
          detail: this.assignmentsSummary,
        },
      ];
    },

    assignmentCompletionPercentage() {
      return getAssignmentCompletionPercentage(this.ragData);
    },

    assignmentCompletionPercentageLabel() {
      return `${this.assignmentCompletionPercentage}%`;
    },

    assignmentsSummary() {
      return getAssignmentsSummary(this.ragData);
    },

    isOnABreak() {
      return isOnABreak(this.ragData);
    },

    bilMetricDetail() {
      return getBilMetricDetail(this.ragData, date => this.parseTimestamp(date, undefined, 'do MMM yyyy'));
    },

    oofMetricValue() {
      return getOofMetricValue(this.ragData);
    },

    fundingMetricDetail() {
      return getFundingMetricDetail(this.ragData, date => this.parseTimestamp(date, undefined, 'do MMM yyyy'));
    },

    otjHoursSummary() {
      return getOtjHoursSummary(this.ragData, hours => this.formatOtjHours(hours));
    },

    workshopsSummary() {
      return getWorkshopsSummary(this.ragData);
    },
  },

  watch: {
    studentId() {
      this.getRagData();
    },
    programmeId() {
      this.getRagData();
    },
  },

  mounted() {
    this.getRagData();
  },

  methods: {
    formatOtjHours(hours) {
      if (hours == null) {
        return '-';
      }
      if (hours === 0) {
        return '0h 0m';
      }
      return this.formatTimeSpent(Math.round(hours * 60));
    },

    getRagData() {
      if (this.studentId == null || this.programmeId == null) {
        this.ragData = null;
        this.ready = true;
        return;
      }

      this.ready = false;
      this.$logger.info('Getting RAG data', { studentId: this.studentId, programmeId: this.programmeId }, true);
      this.$http.get(`/api/stats/users/${this.studentId}/programmes/${this.programmeId}/rag`)
        .then(response => {
          this.$logger.info('Got RAG data', { studentId: this.studentId, programmeId: this.programmeId });
          this.ragData = response.data ? {
            ...response.data,
            perc_otj_hours: response.data.perc_otj_hours || 0,
            workshops_completed_percentage: response.data.workshops_completed_percentage || 0,
          } : null;
        })
        .catch(err => {
          if (this.$http.isWarning(err)) {
            this.$logger.warn('Error getting RAG data', { studentId: this.studentId, programmeId: this.programmeId }, err);
          } else {
            this.$logger.error('Error getting RAG data', { studentId: this.studentId, programmeId: this.programmeId }, err);
          }
          this.ragData = null;
        })
        .then(() => {
          this.ready = true;
        });
    },
  },
};
</script>
